/**
 * What a lens may show this user.
 *
 * A lens only ever hides: it shows a page or section exactly when the user's
 * sidebar would. Pages pass the gate the nav catalog records for them; a
 * section passes the classic sidebar's own rule for it (the section's flag,
 * its product's flag, Management for administrators).
 *
 * Module licensing no longer hides anything — the sidebars show an unlicensed
 * page or section with a lock and it opens on the lock screen — so `passes`
 * and `canSeeSection` ignore module gates. What the org OWNS still decides
 * which lenses are worth offering and how many products it holds (`owns`,
 * `ownsSection`, `productCount`): a lens built on modules the org has not
 * bought would be a menu of lock screens.
 */
import { isFeatureEnabled as defaultIsFeatureEnabled } from '../../utils/featureFlags';
import { readNormalizedUserFromStorage } from '../../utils/entitlements';
import { isPrivilegedUser } from '../../utils/permissions';
import {
  PRODUCTS,
  SECTION_MODULE_SLUGS,
  getProductHomePath,
} from '../layout/Sidebar/productConfig';
import { NAV_SECTIONS } from '../../config/nav/catalog';
import { getModuleLicense } from '../../licensing/useModuleLicense';
import STATIC_MODULE_CATALOG from '../../config/modules.generated';

/** Core modules: every tenant holds them, whatever its module list says. */
const CORE_MODULES = new Set(
  (STATIC_MODULE_CATALOG.modules || []).filter((m) => m.core).map((m) => m.slug),
);

/** "SOC Analyst", "soc_analyst" and "soc-analyst" all read as "soc analyst". */
export const normalizeRoleName = (name) => String(name || '')
  .toLowerCase().replace(/[\s_-]+/g, ' ').trim();

const userRoleNames = (user) => {
  const names = [user.role_name, user.role_key];
  (Array.isArray(user.roles) ? user.roles : []).forEach((r) => {
    if (typeof r === 'string') names.push(r);
    else if (r) names.push(r.role, r.name, r.key);
  });
  return [...new Set(names.map(normalizeRoleName).filter(Boolean))];
};

/**
 * Snapshot of what the nav's gates read, taken from the signed-in user.
 * `entitled` holds canonical module slugs (aliases resolved, core included);
 * `applyModuleFilter` is false when nothing is locked (a super admin, or the
 * auth service's ui_module_lock off-switch).
 */
export const readNavAccess = () => {
  const license = getModuleLicense();
  const user = readNormalizedUserFromStorage();
  return {
    entitled: new Set(license.licensed),
    applyModuleFilter: license.enforcing,
    isFeatureEnabled: defaultIsFeatureEnabled,
    isAdmin: isPrivilegedUser(user),
    orgId: user.organization_id != null ? Number(user.organization_id) : null,
    roles: userRoleNames(user),
  };
};

const ownsModule = (slug, access) => {
  const key = String(slug).trim().toLowerCase();
  return !access.applyModuleFilter || CORE_MODULES.has(key) || access.entitled.has(key);
};

/**
 * Does the user pass `gate`? Module gates are not checked here: an unlicensed
 * page still shows (with a lock). Use `owns` for "has the org bought it".
 */
export const passes = (gate, access) => {
  if (!gate) return true;
  if (Array.isArray(gate)) return gate.every((g) => passes(g, access));
  if (gate.feature && !access.isFeatureEnabled(gate.feature)) return false;
  if (gate.admin && !access.isAdmin) return false;
  if (Array.isArray(gate.orgIds) && !gate.orgIds.map(Number).includes(access.orgId)) return false;
  return true;
};

/** `passes`, and the org holds the module(s) the gate names. */
export const owns = (gate, access) => {
  if (!gate) return true;
  if (Array.isArray(gate)) return gate.every((g) => owns(g, access));
  if (!passes(gate, access)) return false;
  if (gate.module && !ownsModule(gate.module, access)) return false;
  if (Array.isArray(gate.anyModule) && gate.anyModule.length > 0
    && !gate.anyModule.some((m) => ownsModule(m, access))) return false;
  return true;
};

const productOf = (sectionKey) => PRODUCTS.find((p) => p.sections.includes(sectionKey)) || null;

/** True when the classic sidebar would draw this section for the user (licensed or locked). */
export const canSeeSection = (sectionKey, access) => {
  const meta = NAV_SECTIONS[sectionKey];
  if (!meta) return false;
  if (!passes(meta.requires, access)) return false;
  const product = productOf(sectionKey);
  if (product?.featureFlag && !access.isFeatureEnabled(product.featureFlag)) return false;
  return true;
};

/** The section's module is one the org holds (a section in no module always is). */
const sectionLicensed = (sectionKey, access) => {
  const mods = SECTION_MODULE_SLUGS[sectionKey] || [];
  return mods.length === 0 || mods.some((m) => ownsModule(m, access));
};

/** canSeeSection, and the org has bought it. */
export const ownsSection = (sectionKey, access) => canSeeSection(sectionKey, access)
  && sectionLicensed(sectionKey, access);

/**
 * How many products the org holds: the product switcher's entries, by the same
 * rule (an entitled section and the product's flag), Agent Atlas not counted.
 * Lenses arrange work across products, so the frame sets a minimum.
 */
export const productCount = (access) => PRODUCTS
  .filter((p) => p.id !== 'agent-atlas')
  .filter((p) => p.sections.some((k) => sectionLicensed(k, access)))
  .filter((p) => !p.featureFlag || access.isFeatureEnabled(p.featureFlag))
  .length;

/** Where a section's module starts (its product's home), or null for a section with no product. */
export const sectionHomePath = (sectionKey, access) => {
  const product = productOf(sectionKey);
  return product ? getProductHomePath(product, access.entitled) || null : null;
};
